/**
 * The white frame that holds a screen's main content (design-system.md §6
 * "Frame card"): 18px radius, 28px padding, a 0.5px hairline border.
 *
 * Every screen uses it except the Contests list, whose table brings its own
 * rounded container.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

interface FrameCardProps {
  children: ReactNode;
  /** Merged over the frame, so a screen can size it without restating it. */
  sx?: SxProps<Theme>;
  /**
   * The frame is the panel on a screen that switches sections — the Contest
   * Overview is the first — so it carries the tab wiring rather than being
   * wrapped in a bare div that would put a second box around the design's one
   * frame. Named explicitly instead of spreading props: these three are what a
   * tabpanel needs, and the rest of the DOM surface stays closed.
   */
  id?: string;
  role?: string;
  'aria-labelledby'?: string;
}

export const FrameCard = ({
  children,
  sx,
  id,
  role,
  'aria-labelledby': ariaLabelledBy,
}: FrameCardProps) => (
  <Box
    id={id}
    role={role}
    aria-labelledby={ariaLabelledBy}
    sx={[
      (theme: Theme) => ({
        bgcolor: theme.tokens.color.surface,
        borderRadius: `${theme.tokens.radius.frame}px`,
        padding: `${theme.tokens.layout.frameCardPadding}px`,
        border: theme.tokens.border.default,
        boxShadow: theme.tokens.shadow.card,
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {children}
  </Box>
);
